import { type ReactNode } from 'react';
import { ArrowForward } from '@mui/icons-material';
import { Button, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';

// Simple utility function to combine class names
function cn(...classes: (string | undefined | false)[]): string {
  return classes.filter(Boolean).join(' ');
}

interface Column<T = Record<string, unknown>> {
  key: keyof T & string;
  label: string;
  render?: (value: T[keyof T], row: T) => ReactNode;
}

interface DataTableProps<T = Record<string, unknown>> {
  title: string;
  columns: Column<T>[];
  data: T[];
  total?: number;
  loading?: boolean;
  onRowClick?: (row: T, event?: React.MouseEvent) => void;
  getRowUrl?: (row: T) => string;
  onViewAll?: () => void;
  emptyMessage?: string;
  className?: string;
}

export function DataTable<T = Record<string, unknown>>({
  title,
  columns,
  data,
  total,
  loading = false,
  onRowClick,
  getRowUrl,
  onViewAll,
  emptyMessage = 'No data available',
  className,
}: DataTableProps<T>) {
  const showViewAll = total && total > data.length;
  const navigate = useNavigate();

  return (
    <div className={cn('bg-white rounded-lg shadow', className)}>
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-medium text-gray-900">{title}</h3>
            {loading && <CircularProgress size={16} />}
          </div>
          {total && (
            <p className="text-sm text-gray-500">
              Showing {data.length} of {total} total
            </p>
          )}
        </div>
        {showViewAll && onViewAll && (
          <Button
            variant="text"
            onClick={onViewAll}
            className="text-blue-600 hover:text-blue-500 text-sm font-medium"
          >
            See More <ArrowForward className="ml-1 h-4 w-4" />
          </Button>
        )}
      </div>

      {data.length === 0 ? (
        <div className="px-6 py-12 flex items-center justify-center">
          {loading ? (
            <CircularProgress size={28} />
          ) : (
            <p className="text-gray-500">{emptyMessage}</p>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {columns.map(column => (
                  <th
                    key={column.key}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.map((row, index) => {
                const rowUrl = getRowUrl?.(row);

                if (rowUrl) {
                  return (
                    <tr
                      key={index}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={event => {
                        if (event.metaKey || event.ctrlKey) {
                          window.open(rowUrl, '_blank');
                          return;
                        }
                        navigate(rowUrl);
                      }}
                      onAuxClick={event => {
                        if (event.button === 1) {
                          window.open(rowUrl, '_blank');
                        }
                      }}
                    >
                      {columns.map(column => (
                        <td key={column.key} className="px-6 py-4 whitespace-nowrap">
                          {column.render
                            ? column.render(row[column.key as keyof T], row)
                            : String(row[column.key as keyof T] ?? '')}
                        </td>
                      ))}
                    </tr>
                  );
                }

                return (
                  <tr
                    key={index}
                    className={cn('hover:bg-gray-50', onRowClick && 'cursor-pointer')}
                    onClick={event => onRowClick?.(row, event)}
                  >
                    {columns.map(column => (
                      <td key={column.key} className="px-6 py-4 whitespace-nowrap">
                        {column.render
                          ? column.render(row[column.key as keyof T], row)
                          : String(row[column.key as keyof T] ?? '')}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
